import { RestartAlt } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import React from 'react';
import { AUTH_METHODS } from 'shared/constants/building';
import { resetUnitVlans, updateBuildingVlans } from 'shared/api/buildings';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { NumberEditor } from 'shared/components/data-display/editors';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import { VARIANTS } from 'shared/constants';
import { useNotification, useRequest } from 'shared/hooks';
import VTRAudit from 'features/networking/ui/VTRAudit';
import { vlanDistributionTypes } from 'features/networking/core/helper';
import SplitButton from 'shared/components/SplitButton';
import { DataReducerActions } from 'shared/reducers';
import VlanEditDialog from 'features/networking/building/VlanEditDialog';

const descriptions = [{
  label: 'VLAN Start',
  key: 'vlan_range_start',
  editor: NumberEditor,
},
{
  label: 'VLAN End',
  key: 'vlan_range_end',
  editor: NumberEditor,
},
{
  label: 'Ordering',
  key: 'vlanDistributionType',
  editor: withStaticSelect({
    valueAsLabel: true,
    options: vlanDistributionTypes,
    disableClearable: true,
  }),
},
];

/**
 * @param {Object} props
 * @param {Object} props.state - State object containing building data
 * @param {Function} props.dispatch - Dispatch function for reducer actions
 * @param {Function} props.triggerUpdate - Function to trigger update
 * @param {number} props.updateCount - Update counter
 */
export default function VlansPanel({
  state,
  dispatch,
  triggerUpdate,
  updateCount,
}) {
  const { request } = useRequest();
  const { pushError } = useNotification();
  const {
    data: building,
    dirty,
    updatedData,
  } = state;
  const {
    id,
    vlan_range_start: minVlan,
    vlan_range_end: maxVlan,
  } = building;
  const { vlan_distribution_type: vlanDistributionType } = building.dot1x;

  const populateSaveBody = (resetVlans) => {
    const data = { ...updatedData };
    const vlanStart = data?.vlan_range_start || building.vlan_range_start;
    const vlanEnd = data?.vlan_range_end || building.vlan_range_end;

    if (vlanEnd <= vlanStart) {
      pushError('VLAN End must be greater than Vlan Start');
      return null;
    }

    const body = {
      building_id: building.id,
      reset_vlans: resetVlans,
    };
    if (Object.prototype.hasOwnProperty.call(data, 'vlan_range_start')) {
      body.vlan_start = updatedData.vlan_range_start;
    }
    if (Object.prototype.hasOwnProperty.call(data, 'vlan_range_end')) {
      body.vlan_end = updatedData.vlan_range_end;
    }
    if (Object.prototype.hasOwnProperty.call(data, 'vlanDistributionType')) {
      body.vlan_distribution_type = updatedData.vlanDistributionType;
    }
    return body;
  };

  const handleSave = async () => {
    const body = populateSaveBody(false);
    if (!body) return;

    await request(updateBuildingVlans(building.id, body), `Changes saved for ${building.name}`);
    triggerUpdate();
  };

  const handleSaveAndReset = async () => {
    const body = populateSaveBody(true);
    if (!body) return;

    await request(updateBuildingVlans(building.id, body, `Changes saved and VLANS reset for ${building.name}`));
    triggerUpdate();
  };

  const handleResetVlans = async () => {
    await request(resetUnitVlans(building.id), 'VLANs reset');
    triggerUpdate();
  };

  const isDot1x = building.auth_method === AUTH_METHODS.dot1x
    || building.auth_method === AUTH_METHODS.ipv6;

  return (
    <Grid
      container
      sx={{ justifyContent: 'end' }}
    >
      <DataFormatter
        data={{
          ...building,
          vlanDistributionType,
        }}
        descriptions={descriptions}
        editing
        updateValue={(key, value) => dispatch({
          action: DataReducerActions.update,
          payload: {
            key,
            value,
          },
        })}
        updatedData={updatedData}
      />
      <Grid
        size={12}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          mt: '10px',
          flexWrap: 'wrap-reverse',
          gap: 1,
        }}
      >
        {dirty ? (
          <Box sx={{
            mt: 1,
          }}
          >
            <Button
              variant={VARIANTS.outlined}
              onClick={() => dispatch({ action: DataReducerActions.clear })}
              sx={{ mr: 1 }}
            >
              Clear
            </Button>
            <SplitButton
              title="Save"
              onClick={handleSave}
              menuItems={[{
                menuItemClick: handleSaveAndReset,
                menuItemTitle: 'Save and Reset VLANs',
              }]}
            />
          </Box>
        ) : (
          <>
            <VTRAudit
              asButton
              queryKey="building_id"
              queryValue={id}
              disabled={!isDot1x}
            />
            <Button
              startIcon={<RestartAlt />}
              variant={VARIANTS.outlined}
              onClick={handleResetVlans}
              disabled={!minVlan || !maxVlan || minVlan === 0 || maxVlan === 0}
            >
              Reset VLANs
            </Button>
            <VlanEditDialog
              buildingId={id}
              maxVlan={maxVlan || undefined}
              minVlan={minVlan || undefined}
              updateCount={updateCount}
            />
          </>
        )}
      </Grid>
    </Grid>
  );
}
